/**
 * Request Deduplicator
 *
 * Prevents duplicate API requests by caching and deduplicating concurrent calls
 * to the same endpoint. Includes TTL-based cache expiration and automatic cleanup.
 */

class RequestDeduplicator {
  constructor() {
    // Map to store pending requests by key
    this.pendingRequests = new Map();

    // Cache for completed requests with TTL
    this.cache = new Map();

    // Default cache TTL in milliseconds (5 minutes)
    this.defaultTTL = 5 * 60 * 1000;

    // Cleanup interval (1 minute)
    this.cleanupInterval = 60 * 1000;

    // Start periodic cleanup
    this.startCleanup();
  }

  /**
   * Generate a unique key for the request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Request parameters
   * @returns {string} Unique key for the request
   */
  generateKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        if (params[key] !== undefined && params[key] !== null) {
          acc[key] = params[key];
        }
        return acc;
      }, {});

    return `${endpoint}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Execute a request with deduplication
   * @param {string} endpoint - API endpoint
   * @param {Function} requestFn - Function that returns a Promise for the API call
   * @param {Object} options - Options including params and TTL
   * @returns {Promise} Promise that resolves with the API response
   */
  async execute(endpoint, requestFn, options = {}) {
    const { params = {}, ttl = this.defaultTTL, force = false } = options;
    const key = this.generateKey(endpoint, params);

    // If force refresh is requested, clear cache and pending requests
    if (force) {
      this.cache.delete(key);
      this.pendingRequests.delete(key);
    }

    // Check if we have a valid cached response
    const cached = this.cache.get(key);
    if (cached && !this.isExpired(cached)) {
      return Promise.resolve(cached.data);
    }

    // Check if there's already a pending request for this key
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Create new request promise
    const requestPromise = requestFn()
      .then(data => {
        // Cache successful response
        this.cache.set(key, {
          data,
          timestamp: Date.now(),
          ttl
        });

        // Clear from pending requests
        this.pendingRequests.delete(key);

        return data;
      })
      .catch(error => {
        // Clear from pending requests on error
        this.pendingRequests.delete(key);

        // Don't cache errors, throw them
        throw error;
      });

    // Store in pending requests
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  /**
   * Check if a cached entry is expired
   * @param {Object} entry - Cache entry
   * @returns {boolean} True if expired
   */
  isExpired(entry) {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  /**
   * Clear expired cache entries
   */
  cleanup() {
    for (const [key, entry] of this.cache.entries()) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Start periodic cleanup
   */
  startCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.cleanupInterval);
  }

  /**
   * Stop periodic cleanup
   */
  stopCleanup() {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Clear all cache and pending requests
   */
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Clear cache for a specific endpoint
   * @param {string} endpoint - API endpoint to clear
   */
  clearEndpoint(endpoint) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${endpoint}:`)) {
        this.cache.delete(key);
      }
    }

    for (const key of this.pendingRequests.keys()) {
      if (key.startsWith(`${endpoint}:`)) {
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    let validCount = 0;
    let expiredCount = 0;

    for (const entry of this.cache.values()) {
      if (this.isExpired(entry)) {
        expiredCount++;
      } else {
        validCount++;
      }
    }

    return {
      cacheSize: this.cache.size,
      validEntries: validCount,
      expiredEntries: expiredCount,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Create singleton instance
const requestDeduplicator = new RequestDeduplicator();

// Export singleton and class
export default requestDeduplicator;
export { RequestDeduplicator };
